import React from 'react';
import { getAppContext } from '~/lib/AppContext';
import { isAbortError, isProblemDetails } from '~/lib/helper/ErrorHelper';
import RedirectCard from '~/components/RedirectCard';

type SupportSsoRedirectProps = {
    /** Support service name, used only in the fallback log message (e.g. 'Freshdesk', 'Zendesk'). */
    serviceName: string;
    /**
     * Brand-specific client call returning the signed SSO login URL for the support portal. Called
     * with no argument to retry using the endpoint's own defaults, dropping both inbound parameters.
     */
    getSsoUrl: (params?: { returnTo?: string; hostUrl?: string }) => Promise<string>;
    /** Optional portal-relative deep-link path, already extracted from the inbound query string. */
    returnTo?: string;
    /** Optional alternative portal domain, where the provider's callback selects one. */
    hostUrl?: string;
};

/**
 * Browser-facing support SSO hand-off, parameterised per support provider. It is the PanelSite
 * replacement for ClassicPanel's /mpanel/freshdesk and /mpanel/zendesk, which the support
 * providers' SSO "Login URL" points at to authenticate cold arrivals (e.g. ticket-reply email
 * links). An unauthenticated visitor is bounced through the panel login by the standard
 * auth-required redirect (/login?r=...) and returns here; once a session exists the visitor is
 * forwarded to the signed SSO login URL.
 */
export default class SupportSsoRedirect extends React.Component<SupportSsoRedirectProps> {
    componentDidMount() {
        void this.forwardToPortal();
    }

    async forwardToPortal() {
        try {
            const url = await this.props.getSsoUrl({ returnTo: this.props.returnTo, hostUrl: this.props.hostUrl });
            getAppContext().navigate(url, true);
        } catch (error) {
            // 401 has already triggered the login redirect (onAuthRequired); stay on the spinner.
            if (isProblemDetails(error) && error.status === 401) return;

            // The endpoint rejected one of the inbound parameters; retry without them.
            if (isProblemDetails(error) && error.status === 400 && (this.props.returnTo || this.props.hostUrl))
                return this.retryWithDefaults();

            this.fallBackToPortal(error);
        }
    }

    // Our SSO endpoint rejects either inbound parameter — a non-local deep-link path, or a portal domain
    // outside its allowlist — and a rejected value is worth less than an authenticated session, so retry
    // without both rather than guessing which was at fault. The provider's own callback can supply a
    // domain and no path, so this must not be conditional on the path alone.
    async retryWithDefaults() {
        try {
            const url = await this.props.getSsoUrl();
            getAppContext().navigate(url, true);
        } catch (retryError) {
            // The session can expire between the two calls; let the login redirect proceed.
            if (isProblemDetails(retryError) && retryError.status === 401) return;
            this.fallBackToPortal(retryError);
        }
    }

    fallBackToPortal(error: unknown) {
        // An aborted fetch is not a failure: navigating to the portal would fight whatever cancelled it.
        // No caller passes a signal today, so this only covers a browser that reports its own
        // navigation-cancelled fetch as AbortError. Chromium reports `TypeError: Failed to fetch`, which
        // falls through below and may briefly race the navigation that cancelled the request. Threading a
        // signal through `getSsoUrl` and aborting on unmount would not close that gap either, since a
        // page-unloading navigation never runs componentWillUnmount.
        if (isAbortError(error)) return;

        const appContext = getAppContext();
        console.error(`${this.props.serviceName} SSO failed, falling back to the support portal link:`, error);
        appContext.navigate(appContext.config.supportSite, true);
    }

    render() {
        return <RedirectCard message="Redirecting to support..." />;
    }
}
